<template>
  <UserAppChrome v-if="showOutcomeMobileChrome" back-mode="dashboard" />

  <main :class="['public-checkout', { 'public-checkout--app-outcome': showOutcomeMobileChrome }]">
    <section v-if="isOutcome" class="outcome-shell">
      <div class="checkout-panel checkout-panel--center outcome-card">
        <div :class="['outcome-icon', `outcome-icon--${outcomeTone}`]" aria-hidden="true"></div>
        <div class="brand-mark">Fusco Fit</div>
        <p :class="['outcome-status', `outcome-status--${outcomeTone}`]">{{ outcomeStatusLabel }}</p>
        <h1>{{ outcomeTitle }}</h1>
        <p class="lead">{{ outcomeMessage }}</p>

        <div class="outcome-recap" aria-label="Riepilogo ordine">
          <div>
            <span>ID ordine</span>
            <strong>{{ outcomeOrderId || '-' }}</strong>
          </div>
          <div>
            <span>Metodo</span>
            <strong>{{ outcomePaymentLabel }}</strong>
          </div>
          <div>
            <span>Stato</span>
            <strong>{{ outcomePaymentStatus }}</strong>
          </div>
        </div>

        <div v-if="isBankTransferOutcome" class="bank-box">
          <h2>Istruzioni bonifico</h2>
          <a class="btn primary bank-contact" :href="bankTransferWhatsappUrl" target="_blank" rel="noopener noreferrer">
            Clicca qui per aprire una chat con il team amministrazione
          </a>
          <p>Riceverai i dati per effettuare il bonifico.</p>
          <ul>
            <li><span>Causale</span><strong>Ordine {{ outcomeOrderId || '-' }}</strong></li>
          </ul>
        </div>

        <div class="next-steps">
          <h2>{{ nextStepsTitle }}</h2>
          <ol>
            <li v-for="step in outcomeSteps" :key="step">{{ step }}</li>
          </ol>
        </div>

        <div class="outcome-actions">
          <router-link class="btn primary" to="/user">Vai all'area utente</router-link>
          <a v-if="!isBankTransferOutcome" class="btn ghost" href="https://wa.me/393279136921" target="_blank" rel="noopener">Contatta il team</a>
          <a class="btn ghost" :href="landingUrl">Torna a Fusco Fit</a>
        </div>
      </div>
    </section>

    <section v-else-if="!product" class="checkout-panel checkout-panel--center">
      <div class="brand-mark">Fusco Fit</div>
      <h1>Prodotto non disponibile</h1>
      <p class="lead">
        Lo SKU <strong>{{ sku || 'non specificato' }}</strong> non e ancora configurato per il checkout.
      </p>
      <div class="outcome-actions">
        <a class="btn primary" href="https://wa.me/393279136921" target="_blank" rel="noopener">Parla con un consulente</a>
        <a class="btn ghost" :href="landingUrl">Torna alla landing</a>
      </div>
    </section>

    <section v-else class="checkout-grid">
      <aside class="summary-panel">
        <a class="back-link" :href="backUrl">{{ backLabel }}</a>
        <div class="summary-card">
          <span class="eyebrow">{{ product.category }}</span>
          <h1>{{ product.name }}</h1>
          <p class="duration" v-if="product.duration">Durata: {{ product.duration }}</p>
          <div class="price">{{ formatPrice(product.price) }}</div>
          <p class="payment-note">{{ product.paymentText }}</p>
          <dl>
            <div>
              <dt>SKU</dt>
              <dd>{{ product.sku }}</dd>
            </div>
            <div v-if="source">
              <dt>Origine</dt>
              <dd>{{ sourceLabel }}</dd>
            </div>
          </dl>
        </div>
      </aside>

      <div class="checkout-flow">
        <section class="checkout-panel">
          <div class="panel-title">
            <span>1</span>
            <div>
              <h2>{{ user ? 'Account collegato' : 'Accedi o registrati' }}</h2>
              <p>
                {{ user
                  ? 'L’ordine sarà associato automaticamente al tuo profilo Fusco Fit.'
                  : 'Usiamo il tuo account Fusco Fit per collegare ordine e percorso.' }}
              </p>
            </div>
          </div>

          <div v-if="user" class="signed-box">
            <div>
              <strong>{{ profile.email }}</strong>
              <span>Account collegato</span>
            </div>
            <button type="button" class="link-button" @click="logout">Cambia account</button>
          </div>

          <div v-else>
            <div class="auth-tabs" role="tablist" aria-label="Accesso checkout">
              <button type="button" :class="{ active: authMode === 'login' }" @click="authMode = 'login'">Accedi</button>
              <button type="button" :class="{ active: authMode === 'register' }" @click="authMode = 'register'">Registrati</button>
            </div>

            <form v-if="authMode === 'login'" class="form-grid" @submit.prevent="login">
              <label>
                Email
                <input v-model.trim="loginForm.email" type="email" autocomplete="email" required />
              </label>
              <label>
                Password
                <input v-model="loginForm.password" type="password" autocomplete="current-password" required />
              </label>
              <div class="form-actions">
                <button class="btn primary" type="submit" :disabled="authLoading">
                  {{ authLoading ? 'Accesso...' : 'Accedi' }}
                </button>
                <button class="link-button" type="button" :disabled="authLoading" @click="resetPassword">
                  Password dimenticata
                </button>
              </div>
            </form>

            <form v-else class="form-grid" @submit.prevent="register">
              <div class="two-cols">
                <label>
                  Nome
                  <input v-model.trim="registerForm.firstName" autocomplete="given-name" required />
                </label>
                <label>
                  Cognome
                  <input v-model.trim="registerForm.lastName" autocomplete="family-name" required />
                </label>
              </div>
              <label>
                Email
                <input v-model.trim="registerForm.email" type="email" autocomplete="email" required />
              </label>
              <label>
                Telefono
                <input v-model.trim="registerForm.phone" autocomplete="tel" required />
              </label>
              <label>
                Password
                <input v-model="registerForm.password" type="password" autocomplete="new-password" minlength="6" required />
              </label>
              <button class="btn primary" type="submit" :disabled="authLoading">
                {{ authLoading ? 'Creazione...' : 'Crea account' }}
              </button>
            </form>
          </div>

          <p v-if="authMessage" class="message">{{ authMessage }}</p>
          <p v-if="authError" class="message error">{{ authError }}</p>
        </section>

        <section class="checkout-panel" :class="{ muted: !user }">
          <div class="panel-title">
            <span>2</span>
            <div>
              <h2>Dati cliente</h2>
              <p>Li useremo per ricevuta, assistenza e attivazione del percorso.</p>
            </div>
          </div>

          <form class="form-grid" @submit.prevent>
            <div class="two-cols">
              <label>
                Nome
                <input v-model.trim="profile.firstName" :disabled="!user" required />
              </label>
              <label>
                Cognome
                <input v-model.trim="profile.lastName" :disabled="!user" required />
              </label>
            </div>
            <label>
              Email
              <input v-model.trim="profile.email" type="email" disabled />
            </label>
            <label>
              Telefono
              <input v-model.trim="profile.phone" :disabled="!user" required />
            </label>
          </form>
        </section>

        <section class="checkout-panel" :class="{ muted: !user }">
          <div class="panel-title">
            <span>3</span>
            <div>
              <h2>Pagamento</h2>
              <p>Scegli il metodo e concludi l'ordine.</p>
            </div>
          </div>

          <div class="payment-methods">
            <label
              v-for="method in availablePaymentMethods"
              :key="method.value"
              :class="['payment-option', paymentMethod === method.value ? 'active' : '']"
            >
              <input v-model="paymentMethod" type="radio" :value="method.value" :disabled="!user || checkoutLoading" />
              <span>
                <strong>{{ method.label }}</strong>
                <small>{{ method.help }}</small>
              </span>
            </label>
          </div>

          <label v-if="paymentMethod === 'alma' && almaOptions.length" class="installments-field">
            Numero rate Alma
            <select v-model.number="almaInstallments" :disabled="!user || checkoutLoading">
              <option v-for="rate in almaOptions" :key="rate" :value="rate">{{ rate }} rate</option>
            </select>
          </label>

          <div v-if="paymentMethod === 'alma' && almaInstallmentSchedule.length" class="alma-breakdown" aria-live="polite">
            <div class="alma-breakdown-head">
              <span>Piano rateale</span>
              <strong>{{ almaInstallments }} rate mensili</strong>
            </div>
            <ul>
              <li v-for="installment in almaInstallmentSchedule" :key="installment.index">
                <span>{{ installment.label }}</span>
                <strong>{{ installment.amount }}</strong>
              </li>
            </ul>
          </div>

          <label class="terms-row">
            <input v-model="acceptedTerms" type="checkbox" :disabled="!user || checkoutLoading" />
            <span>
              Accetto
              <a :href="legalUrl('termini-e-condizioni')" target="_blank" rel="noopener">termini e condizioni</a>,
              <a :href="legalUrl('privacy-policy')" target="_blank" rel="noopener">privacy policy</a>
              e
              <a :href="legalUrl('scheda-servizi')" target="_blank" rel="noopener">scheda servizi</a>
              Fusco Fit.
            </span>
          </label>

          <button class="btn primary full" type="button" :disabled="!canSubmitCheckout" @click="submitCheckout">
            {{ checkoutLoading ? 'Creo il pagamento...' : checkoutButtonLabel }}
          </button>

          <p v-if="checkoutError" class="message error">{{ checkoutError }}</p>
        </section>
      </div>
    </section>
  </main>
</template>

<script>
import {
  createUserWithEmailAndPassword,
  onAuthStateChanged,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signOut,
  updateProfile,
} from 'firebase/auth';
import {
  doc,
  getDoc,
  serverTimestamp,
  setDoc,
} from 'firebase/firestore';
import { auth, db } from '@/firebaseConfig';
import { getPublicCheckoutFunctionUrl } from '@/lib/env';
import { getPublicCheckoutProduct } from '@/lib/publicCheckoutProducts';
import UserAppChrome from '@/components/utenti/UserAppChrome.vue';

const PAYMENT_LABELS = {
  paypal: {
    label: 'PayPal',
    help: 'Pagamento immediato con PayPal o carta.',
  },
  alma: {
    label: 'Alma',
    help: 'Pagamento rateale dove disponibile.',
  },
  bonifico: {
    label: 'Bonifico',
    help: 'Ricevi le istruzioni e confermiamo dopo accredito.',
  },
};
const PUBLIC_PAYMENT_METHODS = Object.freeze(Object.keys(PAYMENT_LABELS));

export default {
  name: 'PublicCheckout',
  components: { UserAppChrome },
  data() {
    return {
      isOutcomeMobileViewport: false,
      user: null,
      unsubscribeAuth: null,
      authMode: 'login',
      authLoading: false,
      authError: '',
      authMessage: '',
      checkoutLoading: false,
      checkoutError: '',
      paymentMethod: 'paypal',
      almaInstallments: '',
      acceptedTerms: false,
      loginForm: {
        email: '',
        password: '',
      },
      registerForm: {
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        password: '',
      },
      profile: {
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
      },
    };
  },
  computed: {
    sku() {
      return String(this.$route.query.sku || '').trim().toLowerCase();
    },
    source() {
      return String(this.$route.query.source || 'fuscofit').trim();
    },
    product() {
      return getPublicCheckoutProduct(this.sku);
    },
    almaOptions() {
      return Array.isArray(this.product?.almaInstallmentsOptions)
        ? this.product.almaInstallmentsOptions
        : [];
    },
    availablePaymentMethods() {
      const allowed = Array.isArray(this.product?.allowedPayments) ? this.product.allowedPayments : [];
      return allowed
        .map((value) => String(value || '').trim().toLowerCase())
        .filter((value) => PUBLIC_PAYMENT_METHODS.includes(value))
        .map((value) => ({ value, ...PAYMENT_LABELS[value] }));
    },
    checkoutButtonLabel() {
      if (this.paymentMethod === 'bonifico') return 'Registra ordine';
      if (this.paymentMethod === 'alma') return 'Procedi con Alma';
      return 'Procedi con PayPal';
    },
    canSubmitCheckout() {
      return Boolean(
        this.user &&
        this.product &&
        this.profile.firstName &&
        this.profile.lastName &&
        this.profile.email &&
        this.profile.phone &&
        this.paymentMethod &&
        this.acceptedTerms &&
        !this.checkoutLoading
      );
    },
    publicSiteBaseUrl() {
      if (typeof window === 'undefined') return 'https://www.fusco.fit';
      const { hostname, pathname } = window.location;
      if (hostname === 'www.valeriobianconi.it' && pathname.startsWith('/fuscofit')) {
        return 'https://www.valeriobianconi.it/fuscofit';
      }
      return 'https://www.fusco.fit';
    },
    landingUrl() {
      return `${this.publicSiteBaseUrl}/`;
    },
    isAppServicesSource() {
      return this.source.toLowerCase() === 'app-servizi';
    },
    backUrl() {
      return this.isAppServicesSource
        ? this.$router.resolve({ name: 'UserServices' }).href
        : this.landingUrl;
    },
    backLabel() {
      return this.isAppServicesSource ? 'Torna ai servizi' : 'Torna a Fusco Fit';
    },
    sourceLabel() {
      return this.isAppServicesSource ? 'Fusco Fit Connect (logo nero)' : this.source;
    },
    almaInstallmentSchedule() {
      if (this.paymentMethod !== 'alma') return [];
      const installments = Number(this.almaInstallments);
      const price = Number(this.product?.price);
      if (!Number.isInteger(installments) || installments < 2 || !Number.isFinite(price)) return [];

      const totalCents = Math.round(price * 100);
      const baseCents = Math.floor(totalCents / installments);
      const extraCents = totalCents - baseCents * installments;

      return Array.from({ length: installments }, (_, index) => {
        const amountCents = baseCents + (index < extraCents ? 1 : 0);
        return {
          index: index + 1,
          label: index === 0 ? 'Oggi paghi' : `${index + 1} rata`,
          amount: this.formatPrice(amountCents / 100),
        };
      });
    },
    routeMode() {
      if (this.$route.path === '/checkout/success') return 'success';
      if (this.$route.path === '/checkout/cancel') return 'cancel';
      return 'checkout';
    },
    isOutcome() {
      return this.routeMode === 'success' || this.routeMode === 'cancel';
    },
    showOutcomeMobileChrome() {
      return this.isOutcome && this.isOutcomeMobileViewport;
    },
    outcomeOrderId() {
      return String(this.$route.query.orderId || '').trim();
    },
    bankTransferWhatsappUrl() {
      const orderReference = this.outcomeOrderId ? `l'ordine ${this.outcomeOrderId}` : 'un ordine';
      const message = `Ciao, ho registrato ${orderReference} e vorrei ricevere i dati per effettuare il bonifico. Grazie!`;
      return `https://wa.me/393279136921?text=${encodeURIComponent(message)}`;
    },
    isBankTransferOutcome() {
      return this.routeMode === 'success' && this.$route.query.method === 'bonifico';
    },
    isGiftBankTransferOutcome() {
      return this.isBankTransferOutcome && this.$route.query.type === 'gift';
    },
    outcomeMethod() {
      return String(this.$route.query.method || this.$route.query.provider || '').trim().toLowerCase();
    },
    outcomeTone() {
      if (this.routeMode === 'cancel') return 'cancel';
      if (this.isBankTransferOutcome) return 'pending';
      return 'success';
    },
    outcomeStatusLabel() {
      if (this.routeMode === 'cancel') return 'Pagamento non completato';
      if (this.isBankTransferOutcome) return 'Ordine registrato';
      if (this.outcomeMethod === 'alma') return 'Richiesta pagamento ricevuta';
      return 'Pagamento confermato';
    },
    outcomePaymentLabel() {
      if (this.outcomeMethod === 'paypal') return 'PayPal';
      if (this.outcomeMethod === 'alma') return 'Alma';
      if (this.outcomeMethod === 'bonifico') return 'Bonifico bancario';
      return 'Da verificare';
    },
    outcomePaymentStatus() {
      if (this.routeMode === 'cancel') return 'Annullato';
      if (this.isBankTransferOutcome) return 'In attesa di accredito';
      if (this.outcomeMethod === 'alma') return 'In verifica';
      return 'Completato';
    },
    outcomeTitle() {
      if (this.routeMode === 'cancel') return 'Pagamento annullato';
      if (this.isBankTransferOutcome) return 'Ordine ricevuto';
      if (this.outcomeMethod === 'alma') return 'Pagamento Alma avviato';
      return 'Pagamento completato';
    },
    outcomeMessage() {
      if (this.routeMode === 'cancel') {
        return 'Il pagamento non e stato completato. Puoi tornare alla landing o contattare il team.';
      }
      if (this.$route.query.provider === 'alma') {
        return 'Abbiamo ricevuto la richiesta. La conferma Alma puo richiedere qualche minuto.';
      }
      if (this.isBankTransferOutcome) {
        return `Ordine ${this.outcomeOrderId || ''} creato. Completa il bonifico con le istruzioni qui sotto.`;
      }
      return 'Pagamento completato. Riceverai le prossime istruzioni via email.';
    },
    nextStepsTitle() {
      return this.routeMode === 'cancel' ? 'Cosa puoi fare ora' : 'Prossimi passi';
    },
    outcomeSteps() {
      if (this.routeMode === 'cancel') {
        return [
          'Puoi riprovare il pagamento dalla landing o dall\'area utente.',
          'Se hai bisogno di assistenza, contatta il team Fusco Fit.',
        ];
      }
      if (this.isBankTransferOutcome) {
        return [
          'Esegui il bonifico usando esattamente la causale indicata.',
          'Invia la ricevuta a team@fusco.fit per accelerare la verifica.',
          this.isGiftBankTransferOutcome
            ? 'Dopo la conferma dell’accredito del bonifico, il team aggiungerà il valore della gift al saldo Fusco Fit del destinatario.'
            : 'Dopo l\'accredito il team attiverà il percorso sul tuo account.',
        ];
      }
      if (this.outcomeMethod === 'alma') {
        return [
          'Attendi la conferma definitiva di Alma.',
          'Riceverai le prossime istruzioni via email.',
          'L\'ordine sara collegato al tuo account Fusco Fit.',
        ];
      }
      return [
        'Riceverai le prossime istruzioni via email.',
        'L\'ordine e collegato al tuo account Fusco Fit.',
        'Puoi entrare nell\'area utente per seguire il percorso.',
      ];
    },
  },
  watch: {
    product: {
      immediate: true,
      handler() {
        this.applyProductDefaults();
      },
    },
  },
  mounted() {
    this.updateOutcomeMobileViewport();
    window.addEventListener('resize', this.updateOutcomeMobileViewport, { passive: true });
    this.unsubscribeAuth = onAuthStateChanged(auth, async (user) => {
      this.user = user || null;
      this.authError = '';
      if (user) {
        await this.loadUserProfile(user);
      }
    });
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.updateOutcomeMobileViewport);
    if (this.unsubscribeAuth) this.unsubscribeAuth();
  },
  methods: {
    updateOutcomeMobileViewport() {
      this.isOutcomeMobileViewport = window.matchMedia('(max-width: 700px)').matches;
    },
    legalUrl(slug) {
      return `${this.publicSiteBaseUrl}/${slug}/`;
    },
    applyProductDefaults() {
      const available = this.availablePaymentMethods.map((method) => method.value);
      if (!available.includes(this.paymentMethod)) {
        this.paymentMethod = available[0] || '';
      }
      if (this.product?.defaultAlmaInstallments) {
        this.almaInstallments = this.product.defaultAlmaInstallments;
      } else {
        this.almaInstallments = this.almaOptions[0] || '';
      }
    },
    formatPrice(value) {
      const n = Number(value);
      if (!Number.isFinite(n)) return '0,00 EUR';
      return n.toLocaleString('it-IT', { style: 'currency', currency: 'EUR' });
    },
    splitDisplayName(name = '') {
      const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
      return {
        firstName: parts.shift() || '',
        lastName: parts.join(' ') || '',
      };
    },
    async loadUserProfile(user) {
      const fallback = this.splitDisplayName(user.displayName || '');
      const nextProfile = {
        firstName: fallback.firstName,
        lastName: fallback.lastName,
        email: user.email || '',
        phone: '',
      };

      try {
        const snap = await getDoc(doc(db, 'users', user.uid));
        if (snap.exists()) {
          const data = snap.data() || {};
          nextProfile.firstName = data.name || data.firstName || nextProfile.firstName;
          nextProfile.lastName = data.surname || data.lastName || nextProfile.lastName;
          nextProfile.email = data.email || user.email || nextProfile.email;
          nextProfile.phone = data.phone || data.mobile || nextProfile.phone;
        }
      } catch (err) {
        console.warn('Errore recupero profilo checkout:', err);
      }

      this.profile = nextProfile;
      if (!this.loginForm.email) this.loginForm.email = nextProfile.email;
    },
    async login() {
      this.authLoading = true;
      this.authError = '';
      this.authMessage = '';
      try {
        await signInWithEmailAndPassword(auth, this.loginForm.email.trim(), this.loginForm.password);
      } catch (err) {
        console.error('Public checkout login error:', err);
        this.authError = 'Email o password non corretti.';
      } finally {
        this.authLoading = false;
      }
    },
    async register() {
      this.authLoading = true;
      this.authError = '';
      this.authMessage = '';
      try {
        const firstName = this.registerForm.firstName.trim();
        const lastName = this.registerForm.lastName.trim();
        const email = this.registerForm.email.trim().toLowerCase();
        const phone = this.registerForm.phone.trim();
        if (!firstName || !lastName || !email || !phone) {
          throw new Error('Compila tutti i dati richiesti.');
        }

        const credential = await createUserWithEmailAndPassword(auth, email, this.registerForm.password);
        await updateProfile(credential.user, { displayName: `${firstName} ${lastName}`.trim() });
        await setDoc(doc(db, 'users', credential.user.uid), {
          name: firstName,
          surname: lastName,
          email,
          emailLower: email,
          phone,
          uid: credential.user.uid,
          created_time: serverTimestamp(),
          updatedAt: new Date().toISOString(),
        }, { merge: true });
        await this.loadUserProfile(credential.user);
      } catch (err) {
        console.error('Public checkout register error:', err);
        if (err?.code === 'auth/email-already-in-use') {
          this.authError = 'Email gia registrata. Usa la scheda Accedi.';
          this.authMode = 'login';
          this.loginForm.email = this.registerForm.email;
        } else {
          this.authError = err?.message || 'Errore durante la registrazione.';
        }
      } finally {
        this.authLoading = false;
      }
    },
    async resetPassword() {
      this.authError = '';
      this.authMessage = '';
      const email = this.loginForm.email.trim();
      if (!email) {
        this.authError = 'Inserisci la tua email prima di richiedere il reset.';
        return;
      }
      try {
        await sendPasswordResetEmail(auth, email);
        this.authMessage = 'Email di recupero password inviata.';
      } catch (err) {
        console.error('Public checkout reset error:', err);
        this.authError = 'Non sono riuscito a inviare il recupero password.';
      }
    },
    async logout() {
      await signOut(auth);
      this.profile = { firstName: '', lastName: '', email: '', phone: '' };
    },
    async saveProfile() {
      if (!this.user) throw new Error('Accesso richiesto.');
      const email = (this.user.email || this.profile.email || '').trim().toLowerCase();
      await setDoc(doc(db, 'users', this.user.uid), {
        name: this.profile.firstName.trim(),
        surname: this.profile.lastName.trim(),
        email,
        emailLower: email,
        phone: this.profile.phone.trim(),
        uid: this.user.uid,
        updatedAt: new Date().toISOString(),
      }, { merge: true });
      this.profile.email = email;
    },
    async submitCheckout() {
      if (!this.canSubmitCheckout) return;
      this.checkoutLoading = true;
      this.checkoutError = '';
      try {
        await this.saveProfile();
        const token = await this.user.getIdToken();
        const url = getPublicCheckoutFunctionUrl();
        const resp = await fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            sku: this.product.sku,
            source: this.source || 'fuscofit',
            paymentMethod: this.paymentMethod,
            installments: this.paymentMethod === 'alma' ? this.almaInstallments : null,
            customer: {
              firstName: this.profile.firstName,
              lastName: this.profile.lastName,
              email: this.profile.email,
              phone: this.profile.phone,
            },
          }),
        });
        const text = await resp.text();
        let payload = {};
        if (text) {
          try {
            payload = JSON.parse(text);
          } catch {
            payload = { raw: text };
          }
        }
        if (!resp.ok) {
          throw new Error(payload?.error || payload?.raw || 'Errore durante la creazione del pagamento.');
        }
        if (payload.paymentUrl) {
          window.location.href = payload.paymentUrl;
          return;
        }
        if (payload.nextUrl) {
          window.location.href = payload.nextUrl;
          return;
        }
        this.$router.push({ path: '/checkout/success', query: { orderId: payload.orderId || '', method: this.paymentMethod } });
      } catch (err) {
        console.error('Public checkout submit error:', err);
        this.checkoutError = err?.message || 'Errore durante il checkout.';
      } finally {
        this.checkoutLoading = false;
      }
    },
  },
};
</script>

<style scoped>
.public-checkout {
  --ff-bg: #f5f4f0;
  --ff-surface: rgba(255, 255, 255, 0.82);
  --ff-surface-solid: #ffffff;
  --ff-ink: #111110;
  --ff-muted: #5f5f5b;
  --ff-soft: #ececea;
  --ff-line: rgba(17, 17, 16, 0.1);
  --ff-line-strong: rgba(17, 17, 16, 0.18);
  --ff-red: #c8102e;
  --ff-red-dark: #9e0b23;
  --ff-red-soft: rgba(200, 16, 46, 0.08);
  min-height: 100vh;
  background:
    linear-gradient(180deg, rgba(255, 255, 255, 0.54), rgba(255, 255, 255, 0) 280px),
    var(--ff-bg);
  color: var(--ff-ink);
  font-family: "Montserrat", Avenir, Helvetica, Arial, sans-serif;
}

.checkout-grid {
  display: grid;
  grid-template-columns: minmax(292px, 380px) minmax(0, 1fr);
  gap: 24px;
  width: min(1180px, calc(100% - 40px));
  margin: 0 auto;
  padding: 34px 0 44px;
}

.summary-panel {
  position: sticky;
  top: 24px;
  align-self: start;
}

.back-link {
  display: inline-flex;
  align-items: center;
  min-height: 34px;
  margin-bottom: 12px;
  color: var(--ff-muted);
  font-size: 0.72rem;
  font-weight: 900;
  letter-spacing: 0.08em;
  text-decoration: none;
  text-transform: uppercase;
}

.back-link:hover,
.link-button:hover {
  color: var(--ff-red);
}

.summary-card,
.checkout-panel {
  border: 1px solid var(--ff-line);
  border-radius: 8px;
  box-shadow: 0 18px 54px rgba(17, 17, 16, 0.08);
}

.summary-card {
  position: relative;
  overflow: hidden;
  padding: 28px;
  background: var(--ff-ink);
  color: #fff;
}

.summary-card::before {
  content: "";
  position: absolute;
  inset: 0;
  pointer-events: none;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.11), transparent 34%, rgba(200, 16, 46, 0.18));
}

.summary-card > * {
  position: relative;
  z-index: 1;
}

.checkout-panel {
  background: var(--ff-surface);
  backdrop-filter: blur(18px) saturate(1.35);
  -webkit-backdrop-filter: blur(18px) saturate(1.35);
}

.eyebrow,
.brand-mark {
  color: var(--ff-red);
  font-size: 0.72rem;
  font-weight: 900;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

.summary-card h1 {
  margin: 12px 0;
  color: #fff;
  font-size: clamp(1.7rem, 3vw, 2.4rem);
  font-weight: 900;
  line-height: 0.98;
  text-transform: uppercase;
}

.duration,
.payment-note,
.lead {
  color: var(--ff-muted);
  line-height: 1.6;
}

.summary-card .duration,
.summary-card .payment-note {
  color: rgba(255, 255, 255, 0.72);
}

.price {
  margin: 24px 0 8px;
  color: #fff;
  font-size: clamp(2.4rem, 4.6vw, 3.8rem);
  font-weight: 900;
  line-height: 0.95;
}

dl {
  display: grid;
  gap: 10px;
  margin: 24px 0 0;
}

dl div {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  border-top: 1px solid rgba(255, 255, 255, 0.16);
  padding-top: 11px;
}

dt {
  color: rgba(255, 255, 255, 0.54);
  font-size: 0.75rem;
  font-weight: 800;
  text-transform: uppercase;
}

dd {
  margin: 0;
  color: #fff;
  font-weight: 900;
  text-align: right;
  overflow-wrap: anywhere;
}

.checkout-flow {
  display: grid;
  gap: 16px;
}

.checkout-panel {
  padding: 24px;
}

.checkout-panel--center {
  width: min(780px, calc(100% - 40px));
  margin: 44px auto;
  text-align: center;
}

.outcome-shell {
  min-height: 100vh;
  display: grid;
  align-items: center;
  padding: 34px 0;
}

.outcome-card {
  position: relative;
  overflow: hidden;
  color: var(--ff-ink);
}

.outcome-card::before {
  content: "";
  position: absolute;
  inset: 0 0 auto;
  height: 6px;
  background: linear-gradient(90deg, var(--ff-red), var(--ff-ink));
}

.outcome-icon {
  position: relative;
  width: 70px;
  height: 70px;
  display: grid;
  place-items: center;
  margin: 18px auto 18px;
  border-radius: 50%;
  background: #186a3b;
}

.outcome-icon::before,
.outcome-icon::after {
  content: "";
  display: block;
}

.outcome-icon--success::before,
.outcome-icon--pending::before {
  width: 28px;
  height: 15px;
  border-left: 5px solid #fff;
  border-bottom: 5px solid #fff;
  transform: rotate(-45deg) translate(1px, -2px);
}

.outcome-icon--pending {
  background: #a36500;
}

.outcome-icon--pending::before {
  width: 8px;
  height: 32px;
  border: 0;
  border-radius: 4px;
  background: #fff;
  transform: none;
}

.outcome-icon--pending::after {
  width: 8px;
  height: 8px;
  margin-top: 8px;
  border-radius: 50%;
  background: #fff;
}

.outcome-icon--cancel {
  background: #8b1024;
}

.outcome-icon--cancel::before,
.outcome-icon--cancel::after {
  position: absolute;
  width: 34px;
  height: 5px;
  border-radius: 999px;
  background: #fff;
}

.outcome-icon--cancel::before {
  transform: rotate(45deg);
}

.outcome-icon--cancel::after {
  transform: rotate(-45deg);
}

.outcome-status {
  display: inline-flex;
  justify-content: center;
  align-items: center;
  min-height: 30px;
  margin: 12px 0 10px;
  border-radius: 999px;
  padding: 0 13px;
  font-size: 0.72rem;
  font-weight: 900;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.outcome-status--success {
  background: #e8f5ee;
  color: #186a3b;
}

.outcome-status--pending {
  background: #fff2d8;
  color: #7a4a00;
}

.outcome-status--cancel {
  background: #f9dfe4;
  color: #8b1024;
}

.outcome-card h1 {
  margin: 4px 0 8px;
  color: var(--ff-ink);
  font-size: clamp(2rem, 5vw, 3rem);
  font-weight: 900;
  line-height: 0.98;
  text-transform: uppercase;
}

.outcome-card h2,
.outcome-card p,
.outcome-card li,
.outcome-card strong {
  color: var(--ff-ink);
}

.outcome-recap {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
  margin-top: 24px;
}

.outcome-recap div,
.bank-box,
.next-steps {
  border: 1px solid var(--ff-line);
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.72);
}

.outcome-recap div {
  display: grid;
  gap: 6px;
  min-width: 0;
  padding: 14px;
}

.outcome-recap span,
.bank-box span {
  color: var(--ff-muted);
  font-size: 0.68rem;
  font-weight: 900;
  letter-spacing: 0.05em;
  text-transform: uppercase;
}

.outcome-recap strong {
  overflow-wrap: anywhere;
}

.checkout-panel.muted {
  opacity: 0.6;
}

.panel-title {
  display: flex;
  gap: 14px;
  align-items: flex-start;
  margin-bottom: 20px;
}

.panel-title > span {
  width: 34px;
  height: 34px;
  display: grid;
  place-items: center;
  flex: 0 0 auto;
  border-radius: 50%;
  background: var(--ff-ink);
  color: #fff;
  font-weight: 900;
}

.panel-title h2 {
  margin: 0;
  color: var(--ff-ink);
  font-size: 1.32rem;
  font-weight: 900;
  line-height: 1.15;
}

.panel-title p {
  margin: 4px 0 0;
  color: var(--ff-muted);
  line-height: 1.45;
}

.signed-box {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  align-items: center;
  border: 1px solid rgba(200, 16, 46, 0.18);
  border-radius: 8px;
  padding: 14px;
  background: var(--ff-red-soft);
}

.signed-box span {
  display: block;
  color: var(--ff-muted);
  font-size: 0.78rem;
  margin-top: 4px;
}

.auth-tabs {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 6px;
  margin-bottom: 16px;
  padding: 4px;
  border: 1px solid var(--ff-line);
  border-radius: 999px;
  background: rgba(17, 17, 16, 0.04);
}

.auth-tabs button {
  border: 0;
  background: transparent;
  border-radius: 999px;
  padding: 11px 14px;
  color: var(--ff-muted);
  font-weight: 900;
  cursor: pointer;
}

.auth-tabs button.active {
  background: var(--ff-ink);
  color: #fff;
}

.form-grid {
  display: grid;
  gap: 14px;
}

.two-cols {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
}

label {
  display: grid;
  gap: 7px;
  color: var(--ff-ink);
  font-size: 0.82rem;
  font-weight: 900;
}

input,
select {
  width: 100%;
  min-height: 46px;
  box-sizing: border-box;
  border: 1px solid var(--ff-line-strong);
  border-radius: 8px;
  background: #fff;
  color: var(--ff-ink);
  padding: 12px 13px;
  font: inherit;
  outline: none;
  transition: border-color 0.2s, box-shadow 0.2s, background 0.2s;
}

input:focus,
select:focus {
  border-color: rgba(200, 16, 46, 0.58);
  box-shadow: 0 0 0 4px rgba(200, 16, 46, 0.1);
}

input:disabled,
select:disabled {
  background: #eeeeea;
  color: #777774;
}

.form-actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
}

.btn {
  display: inline-flex;
  justify-content: center;
  align-items: center;
  min-height: 44px;
  border-radius: 999px;
  border: 1px solid transparent;
  padding: 0 20px;
  font-weight: 900;
  text-decoration: none;
  cursor: pointer;
  transition: transform 0.16s, background 0.2s, border-color 0.2s;
}

.btn:hover:not(:disabled) {
  transform: translateY(-1px);
}

.btn.primary {
  background: var(--ff-red);
  color: #fff;
}

.btn.primary:hover:not(:disabled) {
  background: var(--ff-red-dark);
}

.btn.ghost {
  background: #fff;
  color: var(--ff-ink);
  border-color: var(--ff-line-strong);
}

.btn.full {
  width: 100%;
  margin-top: 16px;
}

.btn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.link-button {
  border: 0;
  background: transparent;
  color: var(--ff-red);
  font-weight: 900;
  cursor: pointer;
  padding: 0;
}

.payment-methods {
  display: grid;
  gap: 10px;
}

.payment-option {
  grid-template-columns: auto 1fr;
  align-items: center;
  border: 1px solid var(--ff-line);
  border-radius: 8px;
  padding: 14px;
  background: rgba(255, 255, 255, 0.64);
  cursor: pointer;
  transition: border-color 0.2s, box-shadow 0.2s, background 0.2s;
}

.payment-option.active {
  border-color: rgba(200, 16, 46, 0.55);
  background: var(--ff-red-soft);
  box-shadow: 0 0 0 1px rgba(200, 16, 46, 0.16);
}

.payment-option input {
  width: auto;
  min-height: auto;
  accent-color: var(--ff-red);
}

.payment-option small {
  display: block;
  color: var(--ff-muted);
  font-weight: 600;
  margin-top: 3px;
}

.installments-field {
  margin-top: 14px;
}

.alma-breakdown {
  margin-top: 14px;
  border: 1px solid rgba(200, 16, 46, 0.18);
  border-radius: 8px;
  background: var(--ff-red-soft);
  padding: 14px;
}

.alma-breakdown-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  margin-bottom: 10px;
}

.alma-breakdown-head span {
  color: var(--ff-muted);
  font-size: 0.72rem;
  font-weight: 900;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.alma-breakdown-head strong {
  color: var(--ff-ink);
  font-size: 0.88rem;
  font-weight: 900;
  white-space: nowrap;
}

.alma-breakdown ul {
  display: grid;
  gap: 8px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.alma-breakdown li {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  border-top: 1px solid rgba(17, 17, 16, 0.08);
  padding-top: 8px;
}

.alma-breakdown li span {
  color: var(--ff-muted);
  font-weight: 800;
}

.alma-breakdown li strong {
  color: var(--ff-ink);
  font-weight: 900;
  white-space: nowrap;
}

.terms-row {
  grid-template-columns: auto 1fr;
  align-items: start;
  margin-top: 16px;
  font-size: 0.82rem;
  color: var(--ff-muted);
}

.terms-row input {
  width: auto;
  min-height: auto;
  margin-top: 2px;
  accent-color: var(--ff-red);
}

.terms-row a {
  color: var(--ff-red);
  font-weight: 900;
  text-decoration: none;
}

.terms-row a:hover {
  color: var(--ff-red-dark);
  text-decoration: underline;
  text-underline-offset: 3px;
}

.message {
  margin: 14px 0 0;
  color: #186a3b;
  font-weight: 800;
}

.message.error {
  color: #b00020;
}

.bank-box {
  margin: 24px auto 0;
  text-align: left;
  color: var(--ff-ink);
  padding: 18px;
}

.bank-box h2,
.next-steps h2 {
  margin: 0 0 10px;
  color: var(--ff-ink);
  font-size: 1.15rem;
  font-weight: 900;
}

.bank-contact {
  box-sizing: border-box;
  width: 100%;
  border-radius: 8px;
  padding: 12px 20px;
  line-height: 1.45;
  text-align: center;
}

.bank-box ul {
  list-style: none;
  padding: 0;
  margin: 14px 0 0;
  display: grid;
  gap: 10px;
}

.bank-box li {
  display: grid;
  gap: 4px;
}

.next-steps {
  margin-top: 18px;
  text-align: left;
  padding: 18px;
  color: var(--ff-ink);
}

.next-steps ol {
  display: grid;
  gap: 10px;
  margin: 0;
  padding-left: 20px;
  line-height: 1.45;
}

.outcome-actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 12px;
  margin-top: 24px;
}

@media (max-width: 920px) {
  .checkout-grid {
    grid-template-columns: 1fr;
    padding: 18px 0 30px;
  }

  .summary-panel {
    position: static;
  }

  .summary-card h1 {
    font-size: 1.7rem;
  }

  .price {
    font-size: 2.4rem;
  }
}

@media (max-width: 560px) {
  .checkout-grid,
  .checkout-panel--center {
    width: min(100% - 24px, 780px);
  }

  .checkout-panel--center {
    margin: 20px auto;
  }

  .outcome-card h1 {
    font-size: 1.9rem;
  }

  .outcome-recap {
    grid-template-columns: 1fr;
  }

  .two-cols {
    grid-template-columns: 1fr;
  }

  .checkout-panel,
  .summary-card {
    padding: 18px;
  }

  .signed-box,
  .form-actions,
  .outcome-actions {
    align-items: stretch;
    flex-direction: column;
  }

  .alma-breakdown-head,
  .alma-breakdown li {
    align-items: flex-start;
  }

  .btn,
  .link-button {
    width: 100%;
  }
}

@media (max-width: 700px) {
  .public-checkout--app-outcome {
    min-height: auto;
  }

  .public-checkout--app-outcome .outcome-shell {
    min-height: calc(100dvh - 50px - env(safe-area-inset-top, 0px));
    align-items: start;
    padding: 12px 0 calc(82px + max(8px, env(safe-area-inset-bottom, 0px)));
  }

  .public-checkout--app-outcome .checkout-panel--center {
    box-sizing: border-box;
    width: min(calc(100% - 32px), 620px);
    margin: 0 auto;
    padding: 22px 18px;
    border-radius: 20px;
  }

  .public-checkout--app-outcome .outcome-card::before {
    height: 4px;
  }

  .public-checkout--app-outcome .outcome-icon {
    width: 62px;
    height: 62px;
    margin: 10px auto 14px;
  }

  .public-checkout--app-outcome .outcome-card h1 {
    font-size: clamp(1.62rem, 8vw, 2rem);
    line-height: 1.02;
  }

  .public-checkout--app-outcome .lead {
    margin-right: auto;
    margin-left: auto;
    font-size: 0.98rem;
    line-height: 1.5;
    overflow-wrap: anywhere;
  }

  .public-checkout--app-outcome .outcome-recap {
    margin-top: 20px;
  }

  .public-checkout--app-outcome .outcome-recap div,
  .public-checkout--app-outcome .bank-box,
  .public-checkout--app-outcome .next-steps {
    border-radius: 14px;
  }

  .public-checkout--app-outcome .bank-box,
  .public-checkout--app-outcome .next-steps {
    padding-right: 16px;
    padding-left: 16px;
  }
}
</style>
